import type { SenderMailMessage, SenderMailPreview } from "@shared/api";

export interface SenderMailState {
  selected: SenderMailMessage | null;
  approval: SenderMailPreview | null;
  submission: { message: SenderMailPreview; workId: string } | null;
  completed: string[];
}
export const initialSenderMailState: SenderMailState = {
  selected: null,
  approval: null,
  submission: null,
  completed: [],
};
export type SenderMailEvent =
  | { type: "loaded"; messages: SenderMailMessage[] }
  | { type: "select"; message: SenderMailMessage }
  | { type: "arm"; message: SenderMailPreview }
  | { type: "submitted"; message: SenderMailPreview; workId: string }
  | { type: "cancel" }
  | { type: "next"; messages: SenderMailMessage[] };

/** A refreshed list never changes the reviewed/submitted target. Only an owner's choice does. */
export function senderMailState(state: SenderMailState, event: SenderMailEvent): SenderMailState {
  switch (event.type) {
    case "loaded":
      return state.selected || state.submission
        ? state
        : {
            ...state,
            selected: event.messages.find((m) => !state.completed.includes(m.key)) ?? null,
          };
    case "select":
      return state.submission ? state : { ...state, selected: event.message, approval: null };
    case "arm":
      return state.submission || event.message.key !== state.selected?.key
        ? state
        : { ...state, approval: event.message };
    case "submitted":
      return event.message.key !== state.approval?.key ||
        event.message.spamFingerprint !== state.approval.spamFingerprint
        ? state
        : { ...state, submission: { message: state.approval, workId: event.workId } };
    case "cancel":
      return { ...state, approval: null };
    case "next": {
      if (!state.submission) return state;
      const completed = [...new Set([...state.completed, state.submission.message.key])];
      return {
        completed,
        selected: event.messages.find((m) => !completed.includes(m.key)) ?? null,
        approval: null,
        submission: null,
      };
    }
  }
}

/** Keep the selected option visible even if a focus refetch removes it from the server list. */
export function senderMailChoices(
  state: SenderMailState,
  messages: SenderMailMessage[],
): SenderMailMessage[] {
  const items = messages.filter((m) => !state.completed.includes(m.key));
  return state.selected && !items.some((m) => m.key === state.selected!.key)
    ? [state.selected, ...items]
    : items;
}
